import { useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, Eye, Mail, Wallet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMasterWorkspaces, useImpersonateWorkspace } from '../hooks/useMasterData';
import { colorFromSlug, initialsOf, statusLabel, statusTone } from '../lib/workspace-format';
import { formatRelativeTime } from '../lib/audit-format';

export function MasterWorkspaceDetailScreen() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const { id } = useParams({ from: '/m/master/workspaces/$id' });
  const { data, isLoading } = useMasterWorkspaces();
  const impersonate = useImpersonateWorkspace();

  const ws = data?.items.find((w) => w.id === id);

  function onImpersonate() {
    if (!ws) return;
    impersonate.mutate(ws.id, {
      onSuccess: () => {
        toast.success(`Impersonando ${ws.name}`);
        // Após refresh do auth, /m/master detecta isImpersonating e redireciona /m
        navigate({ to: '/m' });
      },
      onError: (err) => {
        toast.error((err as Error).message || 'Falha ao impersonar');
      },
    });
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0 18px 28px', display: 'grid', placeItems: 'center', height: 240 }}>
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--lilac-deep)' }} />
      </div>
    );
  }

  if (!ws) {
    return (
      <div style={{ padding: '0 18px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <BackHeader title="Workspace" onBack={() => navigate({ to: '/m/master/workspaces' })} />
        <div
          style={{
            padding: 14,
            background: 'var(--card-soft)',
            borderRadius: 12,
            fontSize: 13,
            color: 'var(--fg-muted)',
          }}
        >
          Workspace não encontrado.
        </div>
      </div>
    );
  }

  const tone = statusTone(ws.status);
  const label = statusLabel(ws.status);
  const color = colorFromSlug(ws.slug);
  const suspended = ws.status === 'SUSPENDED';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
      <BackHeader title={ws.name} sub="Workspace" onBack={() => navigate({ to: '/m/master/workspaces' })} />

      {/* Card identificação */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: 16,
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 18,
        }}
      >
        <span
          style={{
            width: 52,
            height: 52,
            borderRadius: 15,
            background: color,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 17,
            fontWeight: 600,
            flex: '0 0 52px',
          }}
        >
          {initialsOf(ws.name)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 9px',
                borderRadius: 999,
                fontSize: 10.5,
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                background: `var(--${tone}-soft)`,
                color: `var(--${tone})`,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--${tone})` }} />
              {label}
            </span>
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--fg-muted)',
            }}
          >
            /{ws.slug}
          </div>
        </div>
      </div>

      {/* Botão impersonar */}
      <button
        disabled={suspended || impersonate.isPending}
        onClick={onImpersonate}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '14px 16px',
          borderRadius: 14,
          border: 0,
          background: suspended ? 'var(--card-soft)' : 'var(--lilac-deep)',
          color: suspended ? 'var(--fg-dim)' : '#fff',
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          cursor: suspended || impersonate.isPending ? 'not-allowed' : 'pointer',
          opacity: impersonate.isPending ? 0.6 : 1,
          boxShadow: suspended ? 'none' : '0 8px 22px -10px rgba(107,91,174,0.55)',
        }}
      >
        {impersonate.isPending ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} strokeWidth={2} />}
        {impersonate.isPending ? 'Trocando sessão…' : suspended ? 'Workspace suspenso' : 'Impersonar workspace'}
      </button>

      {/* KPIs do workspace */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <MiniStat label="Usuários" value={String(ws.userCount)} />
        <MiniStat label="Status" value={label} />
        <MiniStat label="Criado" value={formatRelativeTime(ws.createdAt)} />
      </div>

      {/* Linhas detalhe */}
      <div
        style={{
          padding: '4px 14px',
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 14,
        }}
      >
        {([
          ['Admin', ws.adminEmail ?? '—'],
          ['Slug', `/${ws.slug}`],
          ['ID', ws.id.slice(0, 12) + '…'],
        ] as const).map(([k, v], i, a) => (
          <div
            key={k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '11px 0',
              borderBottom: i < a.length - 1 ? '1px solid var(--card-stroke)' : 0,
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{k}</span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'right',
                color: 'var(--fg)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 200,
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>

      {/* Ações */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <SecondaryButton icon={<Mail size={14} />} label="Contatar admin" disabled={!ws.adminEmail}
          onClick={() => ws.adminEmail && (window.location.href = `mailto:${ws.adminEmail}`)} />
        <SecondaryButton
          icon={<Wallet size={14} />}
          label="Cobranças"
          onClick={() => navigate({ to: '/m/master/cobrancas' })}
        />
      </div>
    </div>
  );
}

interface BackHeaderProps {
  title: string;
  sub?: string;
  onBack: () => void;
}
function BackHeader({ title, sub, onBack }: BackHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '16px 0 8px',
      }}
    >
      <button
        onClick={onBack}
        aria-label="Voltar"
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: 'var(--card-soft)',
          border: '1px solid var(--card-stroke)',
          color: 'var(--fg)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
        }}
      >
        <ChevronLeft size={16} strokeWidth={1.8} />
      </button>
      <div style={{ minWidth: 0 }}>
        {sub && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              marginBottom: 2,
            }}
          >
            {sub}
          </div>
        )}
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--fg)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: 12,
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 14,
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'var(--fg-muted)',
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          fontWeight: 700,
          marginTop: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--fg)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

interface SecondaryButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}
function SecondaryButton({ icon, label, onClick, disabled }: SecondaryButtonProps) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '12px 14px',
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 14,
        color: disabled ? 'var(--fg-dim)' : 'var(--fg)',
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        font: 'inherit',
      }}
    >
      {icon} {label}
    </button>
  );
}
